/**
 * 커미션 관련 Write 작업 목차
 * 
 * - createCommission() - 커미션 생성
 * - updateCommission() - 커미션 수정
 * - deleteCommission() - 커미션 삭제
 */

import { collection, addDoc, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, deleteObject } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";

/**
 * 커미션 생성
 * @param {Object} commissionData - 커미션 데이터
 * @param {File} thumbnailFile - 썸네일 이미지 파일
 * @returns {Promise<string>} 생성된 커미션 ID
 */
export async function createCommission(commissionData, thumbnailFile) {
  try {
    let thumbnailFileName = commissionData.thumbnail || "";
    
    // 썸네일 파일이 있으면 Storage에 업로드
    if (thumbnailFile) {
      // 파일명 생성 (타임스탬프 + 원본 파일명)
      const timestamp = Date.now();
      const fileExtension = thumbnailFile.name.split('.').pop();
      thumbnailFileName = `commission_${timestamp}.${fileExtension}`;
      
      // Storage에 업로드
      const storageRef = ref(storage, `commission/${thumbnailFileName}`);
      await uploadBytes(storageRef, thumbnailFile);
    }

    // Firestore에 커미션 데이터 저장
    const commissionDoc = {
      category: commissionData.category,
      name: commissionData.name,
      link: commissionData.link,
      description: commissionData.description || "",
      thumbnail: thumbnailFileName,
      createdBy: commissionData.createdBy,
      createdByNickname: commissionData.createdByNickname,
      createdAt: commissionData.createdAt,
    };

    const docRef = await addDoc(collection(firestore, "commissions"), commissionDoc);
    return docRef.id;
  } catch (error) {
    console.error("커미션 생성 오류:", error);
    throw error;
  }
}

/**
 * 커미션 수정
 * @param {string} commissionId - 커미션 ID
 * @param {Object} updateData - 수정할 데이터
 * @param {File} thumbnailFile - 썸네일 이미지 파일 (선택)
 * @returns {Promise<void>}
 */
export async function updateCommission(commissionId, updateData, thumbnailFile) {
  try {
    let thumbnailFileName = updateData.thumbnail || "";
    
    // 새 썸네일 파일이 있으면 Storage에 업로드
    if (thumbnailFile) {
      // 기존 썸네일 삭제 (있는 경우)
      if (updateData.thumbnail && !updateData.thumbnail.startsWith("http")) {
        try {
          const oldStorageRef = ref(storage, `commission/${updateData.thumbnail}`);
          await deleteObject(oldStorageRef);
        } catch (error) {
          console.warn("기존 썸네일 삭제 실패 (무시):", error);
        }
      }
      
      // 새 파일명 생성
      const timestamp = Date.now();
      const fileExtension = thumbnailFile.name.split('.').pop();
      thumbnailFileName = `commission_${timestamp}.${fileExtension}`;
      
      // Storage에 업로드
      const storageRef = ref(storage, `commission/${thumbnailFileName}`);
      await uploadBytes(storageRef, thumbnailFile);
    }

    // Firestore 업데이트
    const updateDocData = {
      category: updateData.category,
      name: updateData.name,
      link: updateData.link,
      description: updateData.description || "",
      thumbnail: thumbnailFileName,
      lastModifiedBy: updateData.lastModifiedBy,
      lastModifiedByNickname: updateData.lastModifiedByNickname,
      lastModifiedAt: updateData.lastModifiedAt,
    };

    await updateDoc(doc(firestore, "commissions", commissionId), updateDocData);
  } catch (error) {
    console.error("커미션 수정 오류:", error);
    throw error;
  }
}

/**
 * 커미션 삭제
 * @param {string} commissionId - 커미션 ID
 * @param {string} thumbnailFileName - 썸네일 파일명 (선택)
 * @returns {Promise<void>}
 */
export async function deleteCommission(commissionId, thumbnailFileName = null) {
  try {
    // Storage에서 썸네일 삭제 (있는 경우)
    if (thumbnailFileName && !thumbnailFileName.startsWith("http")) {
      try {
        const storageRef = ref(storage, `commission/${thumbnailFileName}`);
        await deleteObject(storageRef);
      } catch (error) {
        console.warn("썸네일 삭제 실패 (무시):", error);
      }
    }

    // Firestore에서 커미션 삭제
    await deleteDoc(doc(firestore, "commissions", commissionId));
  } catch (error) {
    console.error("커미션 삭제 오류:", error);
    throw error;
  }
}

